import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  SizingKitShopifyMap,
  RingProSizingKitShopifyMap,
} from 'content/products/ring';
import { uhxVariantIds } from 'content/products/uhx/variantIds';
import { ProductType } from 'components/shared/Cart/types';
import { Cart, CartLine, CartLineAttribute, CartLineInput } from '../types';
import { PolicyCartInput } from '../types';
import { CartPolicyResult } from './types';

export const getAttr = (
  attributes: CartLineAttribute[] | undefined,
  key: string,
): string | undefined => attributes?.find((a) => a.key === key)?.value;

export const pass = (
  input: PolicyCartInput,
  ...reasons: string[]
): CartPolicyResult => ({ input, reasons });

/** The cart as it will be once `input.lineItemsToRemove` has run: an earlier
 * policy's removals (bestBuyPickupExclusive) must not be merged into,
 * swapped or counted by a later one. */
export function cartWithoutPendingRemovals(
  cart: Cart | null,
  input: PolicyCartInput,
): Cart | null {
  const ids = input.lineItemsToRemove;
  if (!cart || !ids?.length) return cart;
  const drop = new Set(ids);
  return {
    ...cart,
    lines: { edges: cart.lines.edges.filter((edge) => !drop.has(edge.node.id)) },
  };
}

export function findExistingCartLine(
  line: CartLineInput,
  cart: Cart | null,
  store: ShopifyStore | null,
): CartLine | null {
  if (!cart) return null;

  const isRing = getAttr(line.attributes, '_product_type') === ProductType.RING;
  const engravingKeys = ['engraving'];
  let match: CartLine | null = null;

  for (const { node } of cart.lines.edges) {
    // A subscription line and a one-time line of the same variant are distinct.
    if (node.sellingPlanAllocation?.sellingPlan.id !== line.sellingPlanId) continue;

    if (node.merchandise.id === line.merchandiseId) {
      match = node;
    }

    if (
      !match &&
      isRing &&
      node.merchandise.id === line.merchandiseId &&
      (line.attributes ?? [])
        .filter((a) => engravingKeys.includes(a.key))
        .every((noteAttr) =>
          node.attributes.some(
            (attr) =>
              attr.key === noteAttr.key && attr.value === noteAttr.value,
          ),
        )
    ) {
      match = node;
    }

    if (
      !match &&
      store &&
      line.merchandiseId === node.merchandise.id &&
      (node.merchandise.id === SizingKitShopifyMap[store]?.shopifyVariantId ||
        node.merchandise.id ===
          RingProSizingKitShopifyMap[store]?.shopifyVariantId ||
        node.merchandise.id === uhxVariantIds[store]?.Year1 ||
        node.merchandise.id === uhxVariantIds[store]?.Year2)
    ) {
      match = node;
    }
  }

  return match;
}

export function parseLineMaxQuantity(
  attributes: CartLineAttribute[] | undefined,
): number | null {
  const raw = getAttr(attributes, '_max_quantity');
  if (!raw) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.floor(parsed);
  return normalized > 0 ? normalized : null;
}

export function upsertMaxQuantityAttribute(
  attributes: CartLineAttribute[],
  maxQuantity: number | null,
): CartLineAttribute[] {
  if (!maxQuantity) return attributes;
  return [
    ...attributes.filter((a) => a.key !== '_max_quantity'),
    { key: '_max_quantity', value: String(maxQuantity) },
  ];
}

export const getMaxQuantityReachedMessage = (maxQuantity: number): string =>
  maxQuantity === 1
    ? 'Item already in cart.'
    : `Maximum quantity is ${maxQuantity} for this item.`;

export function mergeDedupAttributes(
  existing: CartLineAttribute[],
  incoming: CartLineAttribute[] | undefined,
  effectiveMax: number | null,
): CartLineAttribute[] {
  const noteAttributes = (incoming ?? []).filter(
    (a) => a.key !== '_product_type' && a.key !== '_max_quantity',
  );
  const merged = noteAttributes.length
    ? [
        ...existing.filter(
          (a) => !noteAttributes.some((n) => n.key === a.key),
        ),
        ...noteAttributes,
      ]
    : existing;
  return upsertMaxQuantityAttribute(merged, effectiveMax);
}
